import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';
import { Card, Heading, Text, Modal, PageHeading, PlainTable } from '../components/ui';
import { getAdminStats, getAdminUsers, updateAdminUser, createAdminUser, deleteAdminUser, createAdminBase, createAdminRecipe, getEnhancedAdminStats, toggleAdminStatus, toggleBetaStatus, toggleFamilyStatus } from '../services/api';
import UserDetailModal from '../components/UserDetailModal';
import RecipeTimelineChart from '../components/RecipeTimelineChart';
import SessionTimelineChart from '../components/SessionTimelineChart';
import PixelArtLoader from '../components/PixelArtLoader';

const formatNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '0';
    return new Intl.NumberFormat('en-US').format(Number(value));
};

const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'moments ago';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 5) return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;

    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
};

const formatWeekRange = (weekStart) => {
    if (!weekStart) return 'Unknown';
    const start = new Date(weekStart);
    if (Number.isNaN(start.getTime())) return 'Unknown';
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} – ${end.toLocaleDateString('en-US', options)}`;
};

const formatTimeOfDay = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
    });
};

const AdminDashboard = () => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();

    const [stats, setStats] = useState(null);
    const [enhancedStats, setEnhancedStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [userFilter, setUserFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userDirectoryError, setUserDirectoryError] = useState('');
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    const [showCreateKernel, setShowCreateKernel] = useState(false);
    const [showCreateRecipe, setShowCreateRecipe] = useState(false);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserDetail, setShowUserDetail] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeletingUser, setIsDeletingUser] = useState(false);

    // Modal states for graphs
    const [showRecipeChart, setShowRecipeChart] = useState(false);
    const [showSessionChart, setShowSessionChart] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryUsers, setCategoryUsers] = useState([]);
    const [categoryType, setCategoryType] = useState('');
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [firebaseUsers, setFirebaseUsers] = useState(new Set());
    const [orphanedUsers, setOrphanedUsers] = useState([]);
    const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
    const [isCleaningUp, setIsCleaningUp] = useState(false);

    const [newKernel, setNewKernel] = useState({
        title: '',
        slots: {},
        context: '',
        technique_tips: '',
        common_mistakes: '',
        equipment: '',
        difficulty_level: 'intermediate',
        time_range: '',
        temperature_settings: ''
    });

    const [newRecipe, setNewRecipe] = useState({
        name: '',
        category: '',
        cooking_time: 30,
        ingredients: [],
        instructions: { prerequisites: [], steps: [] }
    });

    const [newUser, setNewUser] = useState({
        email: '',
        username: '',
        display_name: '',
        password: '',
        is_admin: false,
        is_family: false,
        is_beta: false
    });

    const fetchAdminData = useCallback(async () => {
        if (!user) {
            return;
        }

        try {
            setLoading(true);
            setError('');
            setUserDirectoryError('');

            const token = await user.getIdToken();
            let resolvedStats = null;

            try {
                const enhancedPayload = await getEnhancedAdminStats(token);
                setEnhancedStats(enhancedPayload);
                resolvedStats = {
                    users: enhancedPayload.users,
                    bases: enhancedPayload.bases,
                    recipes: enhancedPayload.recipes,
                    sessions: enhancedPayload.sessions
                };
            } catch (enhancedError) {
                console.log('Enhanced stats endpoint not available yet', enhancedError?.message || enhancedError);
                setEnhancedStats(null);
            }

            if (!resolvedStats) {
                try {
                    const statsData = await getAdminStats(token);
                    resolvedStats = statsData;
                } catch (statsErr) {
                    console.log('Basic stats endpoint not available yet', statsErr?.message || statsErr);
                }
            }

            if (resolvedStats) {
                setStats(resolvedStats);
            } else {
                setStats(null);
                setError('Unable to load platform metrics');
            }

            try {
                const usersData = await getAdminUsers(token);
                setUsers(usersData.users || []);
                
                // Fetch Firebase users for validation
                await fetchFirebaseUsers(token);
            } catch (userErr) {
                console.error('Error fetching users:', userErr);
                setUserDirectoryError('Unable to load user directory');
            }
        } catch (err) {
            console.error('Error fetching admin data:', err);
            setError('Failed to load admin dashboard');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchAdminData();
    }, [fetchAdminData]);

    const handleUserClick = (user) => {
        setSelectedUser(user);
        setShowUserDetail(true);
    };

    const handleToggleAdmin = async (userId) => {
        try {
            const token = await user.getIdToken();
            await toggleAdminStatus(userId, token);
            await fetchAdminData();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error toggling admin status:', err);
        }
    };

    const handleToggleBeta = async (userId) => {
        try {
            const token = await user.getIdToken();
            await toggleBetaStatus(userId, token);
            await fetchAdminData();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error toggling beta status:', err);
        }
    };

    const handleToggleFamily = async (userId) => {
        try {
            const token = await user.getIdToken();
            await toggleFamilyStatus(userId, token);
            await fetchAdminData();
            // Reload page to show updated status
            window.location.reload();
        } catch (err) {
            console.error('Error toggling family status:', err);
        }
    };

    const handleDeleteUser = (userToDelete) => {
        setUserToDelete(userToDelete);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setIsDeletingUser(true);
            const token = await user.getIdToken();
            await deleteAdminUser(userToDelete.id, token);
            await fetchAdminData();
            setShowDeleteConfirm(false);
            setUserToDelete(null);
        } catch (err) {
            console.error('Error deleting user:', err);
        } finally {
            setIsDeletingUser(false);
        }
    };

    // Fetch Firebase users for validation
    const fetchFirebaseUsers = async (token) => {
        try {
            // Use the same API base URL as other services
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            const response = await fetch(`${API_BASE_URL}/admin/firebase-users`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            // Check if response is HTML (error page) instead of JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Admin API returned non-JSON response:', {
                    status: response.status,
                    statusText: response.statusText,
                    contentType,
                    body: text.substring(0, 200) + (text.length > 200 ? '...' : '')
                });
                return;
            }
            
            if (response.ok) {
                const data = await response.json();
                const firebaseUids = new Set(data.users.map(u => u.uid));
                setFirebaseUsers(firebaseUids);
            } else {
                const errorData = await response.json();
                console.error('Admin API error:', errorData);
            }
        } catch (err) {
            console.error('Error fetching Firebase users:', err);
        }
    };

    // Update orphaned users whenever Firebase users or database users change
    useEffect(() => {
        if (firebaseUsers.size > 0 && users.length > 0) {
            const orphaned = users.filter(user => !firebaseUsers.has(user.id));
            setOrphanedUsers(orphaned);
        }
    }, [firebaseUsers, users]);

    // Handle category button clicks
    const handleCategoryClick = (category) => {
        let filteredUsers = [];
        
        switch (category) {
            case 'admin':
                filteredUsers = users.filter(u => u.is_admin);
                setCategoryType('Admins');
                break;
            case 'family':
                filteredUsers = users.filter(u => u.is_family);
                setCategoryType('Family Users');
                break;
            case 'beta':
                filteredUsers = users.filter(u => u.is_beta);
                setCategoryType('Beta Users');
                break;
            case 'active':
                filteredUsers = users.filter(u => u.session_count > 0);
                setCategoryType('Active Users');
                break;
            default:
                filteredUsers = users;
                setCategoryType('All Users');
        }
        
        setCategoryUsers(filteredUsers);
        setShowCategoryModal(true);
    };

    // Check if user has Firebase auth
    const hasFirebaseAuth = (userId) => {
        return firebaseUsers.has(userId);
    };

    // Cleanup orphaned users
    const cleanupOrphanedUsers = async () => {
        setShowCleanupConfirm(true);
    };

    // Confirm cleanup after reviewing orphaned users
    const confirmCleanupOrphanedUsers = async () => {
        try {
            setIsCleaningUp(true);
            const token = await user.getIdToken();
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_BASE_URL}/admin/cleanup-orphaned-users`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                alert(`Cleanup completed: ${result.message}`);
                setShowCleanupConfirm(false);
                await fetchAdminData(); // Refresh the data
            } else {
                const error = await response.json();
                alert(`Error: ${error.error}`);
            }
        } catch (err) {
            console.error('Error cleaning up orphaned users:', err);
            alert('Failed to cleanup orphaned users');
        } finally {
            setIsCleaningUp(false);
        }
    };

    const handleCreateUser = async () => {
        try {
            setIsCreatingUser(true);
            const token = await user.getIdToken();
            await createAdminUser(newUser, token);
            await fetchAdminData();
            setShowCreateUser(false);
            setNewUser({
                email: '',
                username: '',
                display_name: '',
                password: '',
                is_admin: false,
                is_family: false,
                is_beta: false
            });
        } catch (err) {
            console.error('Error creating user:', err);
        } finally {
            setIsCreatingUser(false);
        }
    };

    const handleCreateKernel = async () => {
        try {
            const token = await user.getIdToken();
            await createAdminBase(newKernel, token);
            await fetchAdminData();
            setShowCreateKernel(false);
            setNewKernel({
                title: '',
                slots: {},
                context: '',
                technique_tips: '',
                common_mistakes: '',
                equipment: '',
                difficulty_level: 'intermediate',
                time_range: '',
                temperature_settings: ''
            });
        } catch (err) {
            console.error('Error creating kernel:', err);
        }
    };

    const handleCreateRecipe = async () => {
        try {
            const token = await user.getIdToken();
            await createAdminRecipe(newRecipe, token);
            await fetchAdminData();
            setShowCreateRecipe(false);
            setNewRecipe({
                name: '',
                category: '',
                cooking_time: 30,
                ingredients: [],
                instructions: { prerequisites: [], steps: [] }
            });
        } catch (err) {
            console.error('Error creating recipe:', err);
        }
    };

    // Memoized data processing
    const processedData = useMemo(() => {
        if (!stats) return null;

        const userStats = stats.users || {};
        const recipeStats = stats.recipes || {};
        const sessionStats = stats.sessions || {};
        const baseStats = stats.bases || {};

        // Process users
        const allUsers = users || [];
        const filteredUsers = allUsers.filter(user => {
            switch (userFilter) {
                case 'admin': return user.is_admin;
                case 'family': return user.is_family;
                case 'beta': return user.is_beta;
                case 'active': return user.active_session_count > 0;
                default: return true;
            }
        });

        // Process enhanced stats if available
        const enhanced = enhancedStats || {};
        const topForkers = enhanced.top_forkers || [];
        const recentForkedRecipes = enhanced.recent_forked_recipes || [];
        const liveSessions = enhanced.live_sessions || [];
        const topCategories = enhanced.top_categories || [];
        const userTiers = stats.userTiers || [];

        // Process weekly data - keep week_start for chart components
        const weeklyRecipeRows = (enhanced.weekly_recipe_data || []).map(row => ({
            week_start: row.week_start,
            week: formatWeekRange(row.week_start),
            total_recipes: (row.original_recipes || 0) + (row.forked_recipes || 0),
            created_recipes: row.original_recipes || 0,
            forked_recipes: row.forked_recipes || 0
        }));

        const weeklySessionRows = (enhanced.weekly_session_data || []).map(row => ({
            week_start: row.week_start,
            week: formatWeekRange(row.week_start),
            sessions_started: (row.completed_sessions || 0) + (row.incomplete_sessions || 0),
            sessions_completed: row.completed_sessions || 0,
            incomplete_sessions: row.incomplete_sessions || 0
        }));

        return {
            userStats,
            recipeStats,
            sessionStats,
            baseStats,
            filteredUsers,
            topForkers,
            recentForkedRecipes,
            liveSessions,
            topCategories,
            weeklyRecipeRows,
            weeklySessionRows,
            userTiers
        };
    }, [stats, users, userFilter, enhancedStats]);

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0">
                <div className="w-full py-8">
                    <Card padding="lg">
                        <PixelArtLoader 
                            message="LOADING ADMIN DASHBOARD"
                            subMessage="Preparing real-time platform metrics..."
                        />
                    </Card>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0">
                <div className="w-full py-8">
                    <Card padding="lg" className="text-center space-y-3">
                        <Heading level={2} className="text-neutral-900 font-mono uppercase tracking-wider">
                            {error}
                        </Heading>
                        <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                            Try refreshing or check the backend service.
                        </Text>
                    </Card>
                </div>
            </div>
        );
    }

    if (!processedData) {
        return (
            <div className="min-h-screen bg-neutral-50 px-0">
                <div className="w-full py-8">
                    <Card padding="lg" className="text-center space-y-3">
                        <Heading level={2} className="text-neutral-900 font-mono uppercase tracking-wider">
                            No data available
                        </Heading>
                        <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                            Unable to load platform metrics.
                        </Text>
                    </Card>
                </div>
            </div>
        );
    }

    const { userStats, recipeStats, sessionStats, baseStats, filteredUsers, topForkers, recentForkedRecipes, liveSessions, topCategories, weeklyRecipeRows, weeklySessionRows, userTiers } = processedData;

    return (
        <div className="min-h-screen bg-neutral-50 px-0">
            <div className="w-full py-8">
                <PageHeading size="hero" className="mb-6">ADMIN CONTROL CENTER</PageHeading>
                
                {/* Two Card Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    
                    {/* Left Card - Platform Overview */}
                    <Card padding="lg" className="bg-neutral-0 hairline space-y-6">
                        <div>
                            <Heading level={3} className="text-neutral-900 font-mono uppercase tracking-wider mb-2">
                                Platform Overview
                            </Heading>
                            <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                                Real-time metrics and system health
                            </Text>
                        </div>

                        {/* Key Metrics Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            <div className="bg-neutral-50 hairline p-4 space-y-2 min-h-[120px] flex flex-col">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    Total Users
                                </Text>
                                <Heading level={4} className="text-neutral-900 text-2xl font-bold flex-1 flex items-center">
                                    {formatNumber(userStats.total_users)}
                                </Heading>
                                <div className="flex flex-wrap gap-1 text-xs font-mono uppercase tracking-wider">
                                    <Text className="text-neutral-900 opacity-60 whitespace-nowrap">
                                        {formatNumber(userStats.admin_users)} admins
                                    </Text>
                                    <Text className="text-neutral-900 opacity-60 whitespace-nowrap">
                                        {formatNumber(userStats.family_users)} family
                                    </Text>
                                    <Text className="text-neutral-900 opacity-60 whitespace-nowrap">
                                        {formatNumber(userStats.beta_users)} beta
                                    </Text>
                                </div>
                            </div>
                            
                            <div className="bg-neutral-50 hairline p-4 space-y-2 min-h-[120px] flex flex-col">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    Total Recipes
                                </Text>
                                <Heading level={4} className="text-neutral-900 text-2xl font-bold flex-1 flex items-center">
                                    {formatNumber(recipeStats.total_recipes)}
                                </Heading>
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    {formatNumber(recipeStats.forked_recipes)} forked
                                </Text>
                            </div>
                            
                            <div className="bg-neutral-50 hairline p-4 space-y-2 min-h-[120px] flex flex-col">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    Completed Sessions
                                </Text>
                                <Heading level={4} className="text-neutral-900 text-2xl font-bold flex-1 flex items-center">
                                    {formatNumber(sessionStats.completed_sessions)}
                                </Heading>
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    {formatNumber(sessionStats.sessions_7d)} started this week
                                </Text>
                            </div>
                            
                            <div className="bg-neutral-50 hairline p-4 space-y-2 min-h-[120px] flex flex-col">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    Onboarding Walkthrough
                                </Text>
                                <Heading level={4} className="text-neutral-900 text-2xl font-bold flex-1 flex items-center">
                                    {formatNumber(userStats.walkthrough_completed)}
                                </Heading>
                                <div className="space-y-1 text-xs font-mono uppercase tracking-wider">
                                    <div className="text-neutral-900 opacity-60">
                                        {formatNumber(userStats.walkthrough_completed)} completed
                                    </div>
                                    <div className="text-neutral-900 opacity-60">
                                        {formatNumber(userStats.walkthrough_in_progress)} in progress
                                    </div>
                                    <div className="text-neutral-900 opacity-60">
                                        {formatNumber(userStats.walkthrough_not_started)} not started
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-neutral-50 hairline p-4 space-y-2 min-h-[120px] flex flex-col">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    Total Bases
                                </Text>
                                <Heading level={4} className="text-neutral-900 text-2xl font-bold flex-1 flex items-center">
                                    {formatNumber(baseStats.total_bases)}
                                </Heading>
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    {formatNumber(baseStats.new_bases_7d)} new (7d)
                                </Text>
                            </div>
                        </div>

                        {/* User Engagement Tiers */}
                        {userTiers && userTiers.length > 0 && (
                            <div className="space-y-3">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    User Engagement Tiers
                                </Text>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                                    {userTiers.map((tier) => {
                                        const tierLabels = {
                                            'new': 'New',
                                            'beginner': 'Beginner',
                                            'active': 'Active',
                                            'power': 'Power',
                                            'super': 'Super'
                                        };
                                        const tierColors = {
                                            'new': 'bg-neutral-100 text-neutral-700',
                                            'beginner': 'bg-blue-100 text-blue-700',
                                            'active': 'bg-green-100 text-green-700',
                                            'power': 'bg-purple-100 text-purple-700',
                                            'super': 'bg-yellow-100 text-yellow-700'
                                        };
                                        return (
                                            <div 
                                                key={tier.tier} 
                                                className={`hairline p-3 ${tierColors[tier.tier] || 'bg-neutral-50 text-neutral-900'}`}
                                            >
                                                <Text className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">
                                                    {tierLabels[tier.tier] || tier.tier}
                                                </Text>
                                                <Heading level={4} className="text-lg font-bold">
                                                    {formatNumber(tier.user_count)}
                                                </Heading>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider italic">
                                    Tiers based on recipe count: New (0), Beginner (1-5), Active (6-20), Power (21-50), Super (50+)
                                </Text>
                            </div>
                        )}

                        {/* Chart Buttons */}
                        <div className="space-y-3">
                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                Analytics
                            </Text>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowRecipeChart(true)}
                                    className="flex-1 px-4 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                >
                                    Recipe Timeline
                                </button>
                                <button
                                    onClick={() => setShowSessionChart(true)}
                                    className="flex-1 px-4 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                >
                                    Session Timeline
                                </button>
                            </div>
                        </div>

                        {/* Admin Actions */}
                        <div className="space-y-3">
                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                Quick Actions
                            </Text>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowCreateUser(true)}
                                    className="px-3 py-2 bg-blue-600 text-neutral-0 font-mono text-xs uppercase tracking-wider hover:opacity-70 transition-opacity"
                                >
                                    Create User
                                </button>
                                <button
                                    onClick={() => setShowCreateKernel(true)}
                                    className="px-3 py-2 bg-neutral-900 text-neutral-0 font-mono text-xs uppercase tracking-wider hover:opacity-70 transition-opacity"
                                >
                                    Create Base
                                </button>
                                <button
                                    onClick={() => setShowCreateRecipe(true)}
                                    className="px-3 py-2 bg-neutral-900 text-neutral-0 font-mono text-xs uppercase tracking-wider hover:opacity-70 transition-opacity"
                                >
                                    Create Recipe
                                </button>
                            </div>
                        </div>
                    </Card>

                    {/* Right Card - User Management */}
                    <Card padding="lg" className="bg-neutral-0 hairline space-y-6">
                        <div>
                            <Heading level={3} className="text-neutral-900 font-mono uppercase tracking-wider mb-2">
                                User Management
                            </Heading>
                            <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                                Manage accounts and permissions
                            </Text>
                        </div>

                        {/* User Filter */}
                        <div className="flex gap-2">
                            {[
                                { id: 'all', label: 'All users' },
                                { id: 'admin', label: 'Admins' },
                                { id: 'family', label: 'Family' },
                                { id: 'beta', label: 'Beta' },
                                { id: 'active', label: 'Active' }
                            ].map((option) => (
                                <button
                                    key={option.id}
                                    onClick={() => handleCategoryClick(option.id)}
                                    className={`px-3 py-1 font-mono text-xs uppercase tracking-wider transition-colors ${
                                        userFilter === option.id
                                            ? 'bg-neutral-900 text-neutral-0'
                                            : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>

                        {/* Cleanup Button - Only show when there are orphaned users */}
                        {orphanedUsers.length > 0 && (
                            <div className="flex justify-between items-center">
                                <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                    {orphanedUsers.length} user{orphanedUsers.length !== 1 ? 's' : ''} without Firebase auth
                                </Text>
                                <button
                                    onClick={cleanupOrphanedUsers}
                                    className="px-3 py-1 bg-red-600 text-white font-mono text-xs uppercase tracking-wider hover:bg-red-700 transition-colors"
                                >
                                    Cleanup Orphaned Users
                                </button>
                            </div>
                        )}

                        {/* User List */}
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {userDirectoryError ? (
                                <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                                    {userDirectoryError}
                                </Text>
                            ) : filteredUsers.length ? (
                                filteredUsers.slice(0, 10).map((account) => (
                                    <div 
                                        key={account.id} 
                                        className={`hairline p-3 flex items-center justify-between gap-4 hover:bg-neutral-100 transition-colors cursor-pointer ${
                                            hasFirebaseAuth(account.id) ? 'bg-neutral-50' : 'bg-red-50 border-red-300'
                                        }`}
                                        onClick={() => handleUserClick(account)}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <Text className={`font-medium truncate ${
                                                hasFirebaseAuth(account.id) ? 'text-neutral-900' : 'text-red-700'
                                            }`}>
                                                {account.display_name || account.username}
                                                {!hasFirebaseAuth(account.id) && (
                                                    <span className="ml-2 text-xs font-mono uppercase tracking-wider text-red-600">
                                                        (NO FIREBASE AUTH)
                                                    </span>
                                                )}
                                            </Text>
                                            <Text className={`text-xs font-mono uppercase tracking-wider truncate ${
                                                hasFirebaseAuth(account.id) ? 'text-neutral-900 opacity-60' : 'text-red-600 opacity-80'
                                            }`}>
                                                {account.email}
                                            </Text>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex gap-1">
                                                {account.is_admin && (
                                                    <span className="inline-block w-3 h-3 rounded-full bg-red-500" title="Admin"></span>
                                                )}
                                                {account.is_family && (
                                                    <span className="inline-block w-3 h-3 rounded-full bg-blue-500" title="Family"></span>
                                                )}
                                                {account.is_beta && (
                                                    <span className="inline-block w-3 h-3 rounded-full bg-purple-500" title="Beta"></span>
                                                )}
                                                {!account.is_admin && !account.is_family && !account.is_beta && (
                                                    <span className="inline-block w-3 h-3 rounded-full bg-neutral-300" title="User"></span>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <Text className="text-neutral-900 font-mono text-xs uppercase tracking-wider">
                                                    {account.plan_type || 'free'} • {formatNumber(account.session_count)} sessions
                                                </Text>
                                                {account.engagement_tier && (
                                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                                        {account.engagement_tier} tier
                                                    </Text>
                                                )}
                                                {account.active_session_count ? (
                                                    <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider">
                                                        {formatNumber(account.active_session_count)} live
                                                    </Text>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                                    No users match the selected filter
                                </Text>
                            )}
                        </div>

                        {filteredUsers.length > 10 && (
                            <Text className="text-neutral-900 opacity-60 text-xs font-mono uppercase tracking-wider text-center">
                                Showing 10 of {filteredUsers.length} users
                            </Text>
                        )}
                    </Card>
                </div>
            </div>

            {/* Modals */}
            <UserDetailModal
                isOpen={showUserDetail}
                onClose={() => setShowUserDetail(false)}
                user={selectedUser}
                currentUser={user}
                onUserUpdate={fetchAdminData}
            />

            {/* Category Users Modal */}
            <Modal
                isOpen={showCategoryModal}
                onClose={() => {
                    setShowCategoryModal(false);
                    setCategorySearchTerm('');
                }}
                title={`${categoryType} (${categoryUsers.length})`}
                maxWidth="max-w-4xl"
            >
                {/* Search Bar */}
                <div className="mb-4">
                    <input
                        type="text"
                        value={categorySearchTerm}
                        onChange={(e) => setCategorySearchTerm(e.target.value)}
                        placeholder="Search users by name or email..."
                        className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 font-mono text-sm"
                    />
                </div>
                
                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {categoryUsers
                        .filter((account) => {
                            if (!categorySearchTerm) return true;
                            const searchLower = categorySearchTerm.toLowerCase();
                            return (
                                (account.display_name || '').toLowerCase().includes(searchLower) ||
                                (account.username || '').toLowerCase().includes(searchLower) ||
                                (account.email || '').toLowerCase().includes(searchLower)
                            );
                        })
                        .map((account) => (
                        <div 
                            key={account.id} 
                            className={`hairline p-4 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors cursor-pointer ${
                                hasFirebaseAuth(account.id) ? 'bg-neutral-0' : 'bg-red-50 border-red-300'
                            }`}
                            onClick={() => {
                                setSelectedUser(account);
                                setShowCategoryModal(false);
                                setShowUserDetail(true);
                            }}
                        >
                            <div className="flex-1 min-w-0">
                                <Text className={`font-medium truncate ${
                                    hasFirebaseAuth(account.id) ? 'text-neutral-900' : 'text-red-700'
                                }`}>
                                    {account.display_name || account.username}
                                    {!hasFirebaseAuth(account.id) && (
                                        <span className="ml-2 text-xs font-mono uppercase tracking-wider text-red-600">
                                            (NO FIREBASE AUTH)
                                        </span>
                                    )}
                                </Text>
                                <Text className={`text-xs font-mono uppercase tracking-wider truncate ${
                                    hasFirebaseAuth(account.id) ? 'text-neutral-900 opacity-60' : 'text-red-600 opacity-80'
                                }`}>
                                    {account.email}
                                </Text>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex gap-1">
                                    {account.is_admin && (
                                        <span className="inline-block w-3 h-3 rounded-full bg-red-500" title="Admin"></span>
                                    )}
                                    {account.is_family && (
                                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500" title="Family"></span>
                                    )}
                                    {account.is_beta && (
                                        <span className="inline-block w-3 h-3 rounded-full bg-purple-500" title="Beta"></span>
                                    )}
                                    {!account.is_admin && !account.is_family && !account.is_beta && (
                                        <span className="inline-block w-3 h-3 rounded-full bg-neutral-300" title="User"></span>
                                    )}
                                </div>
                                <div className="text-right">
                                    <Text className={`font-mono text-xs uppercase tracking-wider ${
                                        hasFirebaseAuth(account.id) ? 'text-neutral-900' : 'text-red-700'
                                    }`}>
                                        {account.plan_type || 'free'} • {formatNumber(account.session_count)} sessions
                                    </Text>
                                    {account.engagement_tier && (
                                        <Text className={`text-xs font-mono uppercase tracking-wider ${
                                            hasFirebaseAuth(account.id) ? 'text-neutral-900 opacity-60' : 'text-red-600 opacity-80'
                                        }`}>
                                            {account.engagement_tier} tier
                                        </Text>
                                    )}
                                    {account.active_session_count ? (
                                        <Text className={`text-xs font-mono uppercase tracking-wider ${
                                            hasFirebaseAuth(account.id) ? 'text-neutral-900 opacity-60' : 'text-red-600 opacity-80'
                                        }`}>
                                            {formatNumber(account.active_session_count)} live
                                        </Text>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>

            {/* Cleanup Confirmation Modal */}
            <Modal
                isOpen={showCleanupConfirm}
                onClose={() => setShowCleanupConfirm(false)}
                title={`Cleanup Orphaned Users (${orphanedUsers.length})`}
                maxWidth="max-w-4xl"
            >
                <div className="space-y-6">
                    <div className="bg-red-50 border border-red-300 p-4">
                        <Text className="text-red-800 font-mono text-sm">
                            ⚠️ WARNING: This will permanently delete {orphanedUsers.length} user{orphanedUsers.length !== 1 ? 's' : ''} from the database.
                            These users exist in the database but not in Firebase authentication.
                        </Text>
                    </div>

                    <div>
                        <Heading level={4} className="text-neutral-900 font-mono uppercase tracking-wider mb-4">
                            Users to be deleted:
                        </Heading>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {orphanedUsers.map((user) => (
                                <div key={user.id} className="bg-red-50 border border-red-300 p-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <Text className="text-red-800 font-medium">
                                                {user.display_name || user.username}
                                            </Text>
                                            <Text className="text-red-600 text-sm font-mono uppercase tracking-wider">
                                                {user.email}
                                            </Text>
                                            <div className="mt-2 space-y-1">
                                                <Text className="text-red-700 text-xs font-mono">
                                                    Joined: {formatDate(user.created_at)}
                                                </Text>
                                                <Text className="text-red-700 text-xs font-mono">
                                                    Last Active: {formatDate(user.updated_at)}
                                                </Text>
                                                <Text className="text-red-700 text-xs font-mono">
                                                    Recipes: {user.recipe_count || 0}
                                                </Text>
                                                <Text className="text-red-700 text-xs font-mono">
                                                    Sessions: {user.session_count || 0}
                                                </Text>
                                            </div>
                                        </div>
                                        <div className="flex gap-1 ml-4">
                                            {user.is_admin && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-red-500" title="Admin"></span>
                                            )}
                                            {user.is_family && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-blue-500" title="Family"></span>
                                            )}
                                            {user.is_beta && (
                                                <span className="inline-block w-3 h-3 rounded-full bg-purple-500" title="Beta"></span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => setShowCleanupConfirm(false)}
                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmCleanupOrphanedUsers}
                            disabled={isCleaningUp}
                            className="px-4 py-2 bg-red-600 text-white font-mono text-sm uppercase tracking-wider hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                            {isCleaningUp ? 'Deleting...' : `Delete ${orphanedUsers.length} User${orphanedUsers.length !== 1 ? 's' : ''}`}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Recipe Timeline Chart Modal */}
            <Modal
                isOpen={showRecipeChart}
                onClose={() => setShowRecipeChart(false)}
                title="Recipe Timeline"
                maxWidth="max-w-4xl"
            >
                <div className="space-y-4">
                    <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                        Weekly recipe creation and forking trends
                    </Text>
                    <RecipeTimelineChart data={weeklyRecipeRows} />
                </div>
            </Modal>

            {/* Session Timeline Chart Modal */}
            <Modal
                isOpen={showSessionChart}
                onClose={() => setShowSessionChart(false)}
                title="Session Timeline"
                maxWidth="max-w-4xl"
            >
                <div className="space-y-4">
                    <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                        Weekly cooking session completion trends
                    </Text>
                    <SessionTimelineChart data={weeklySessionRows} />
                </div>
            </Modal>

            {/* Create User Modal */}
            <Modal
                isOpen={showCreateUser}
                onClose={() => setShowCreateUser(false)}
                title="Create New User"
            >
                <div className="vspace-lg">
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Email</label>
                        <input
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="user@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Username</label>
                        <input
                            type="text"
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="username"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Display Name</label>
                        <input
                            type="text"
                            value={newUser.display_name}
                            onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="Display Name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Password</label>
                        <input
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="Password"
                        />
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_admin"
                                checked={newUser.is_admin}
                                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
                                className="w-4 h-4"
                            />
                            <label htmlFor="is_admin" className="text-sm font-mono uppercase tracking-wider">
                                Admin privileges
                            </label>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_family"
                                checked={newUser.is_family}
                                onChange={(e) => setNewUser({ ...newUser, is_family: e.target.checked })}
                                className="w-4 h-4"
                            />
                            <label htmlFor="is_family" className="text-sm font-mono uppercase tracking-wider">
                                Family user
                            </label>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_beta"
                                checked={newUser.is_beta}
                                onChange={(e) => setNewUser({ ...newUser, is_beta: e.target.checked })}
                                className="w-4 h-4"
                            />
                            <label htmlFor="is_beta" className="text-sm font-mono uppercase tracking-wider">
                                Beta tester
                            </label>
                        </div>
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={handleCreateUser}
                            disabled={isCreatingUser}
                            className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50"
                        >
                            {isCreatingUser ? 'Creating...' : 'Create User'}
                        </button>
                        <button
                            onClick={() => setShowCreateUser(false)}
                            className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Create Base Modal */}
            <Modal
                isOpen={showCreateKernel}
                onClose={() => setShowCreateKernel(false)}
                title="Create Verified Base"
            >
                <div className="vspace-lg">
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Title</label>
                        <input
                            type="text"
                            value={newKernel.title}
                            onChange={(e) => setNewKernel({ ...newKernel, title: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="Enter base title..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Context</label>
                        <textarea
                            value={newKernel.context}
                            onChange={(e) => setNewKernel({ ...newKernel, context: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm h-24"
                            placeholder="Describe the cooking technique or method..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Technique Tips</label>
                        <textarea
                            value={newKernel.technique_tips}
                            onChange={(e) => setNewKernel({ ...newKernel, technique_tips: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm h-24"
                            placeholder="Key tips for mastering this technique..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Common Mistakes</label>
                        <textarea
                            value={newKernel.common_mistakes}
                            onChange={(e) => setNewKernel({ ...newKernel, common_mistakes: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm h-24"
                            placeholder="Common pitfalls to avoid..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Equipment</label>
                        <input
                            type="text"
                            value={newKernel.equipment}
                            onChange={(e) => setNewKernel({ ...newKernel, equipment: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="Required equipment..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Difficulty Level</label>
                        <select
                            value={newKernel.difficulty_level}
                            onChange={(e) => setNewKernel({ ...newKernel, difficulty_level: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                        >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={handleCreateKernel}
                            className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            Create Base
                        </button>
                        <button
                            onClick={() => setShowCreateKernel(false)}
                            className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Create Recipe Modal */}
            <Modal
                isOpen={showCreateRecipe}
                onClose={() => setShowCreateRecipe(false)}
                title="Create Recipe"
            >
                <div className="vspace-lg">
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Recipe Name</label>
                        <input
                            type="text"
                            value={newRecipe.name}
                            onChange={(e) => setNewRecipe({ ...newRecipe, name: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="Enter recipe name..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Category</label>
                        <select
                            value={newRecipe.category}
                            onChange={(e) => setNewRecipe({ ...newRecipe, category: e.target.value })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                        >
                            <option value="">Select category...</option>
                            <option value="Breakfast">Breakfast</option>
                            <option value="Lunch">Lunch</option>
                            <option value="Dinner">Dinner</option>
                            <option value="Dessert">Dessert</option>
                            <option value="Snack">Snack</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-mono uppercase tracking-wider mb-2">Cooking Time (minutes)</label>
                        <input
                            type="number"
                            value={newRecipe.cooking_time}
                            onChange={(e) => setNewRecipe({ ...newRecipe, cooking_time: parseInt(e.target.value) || 30 })}
                            className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm"
                            placeholder="30"
                        />
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={handleCreateRecipe}
                            className="px-6 py-3 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            Create Recipe
                        </button>
                        <button
                            onClick={() => setShowCreateRecipe(false)}
                            className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete User Confirmation Modal */}
            <Modal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Delete User"
            >
                <div className="vspace">
                    <Text>
                        Are you sure you want to delete user "{userToDelete?.display_name || userToDelete?.username}"? 
                        This action cannot be undone and will remove all associated data.
                    </Text>
                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={confirmDeleteUser}
                            disabled={isDeletingUser}
                            className="px-6 py-3 bg-red-600 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50"
                        >
                            {isDeletingUser ? 'Deleting...' : 'Delete User'}
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminDashboard;